import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Eye } from 'lucide-react';
import { Button } from '@attendance/ui/components/Button';
import { ICON } from '@attendance/ui';
import { VIEW_AS_TTL_MINUTES } from '@attendance/shared';
import { useViewAsStore } from '../../stores/viewAsStore.js';

/** Where Exit lands: the list the view was started from. */
export const VIEW_AS_RETURN = '/settings/roles?tab=people';

/**
 * THE VIEW-AS BAND — above everything, on every page, while a Super
 * Administrator is viewing the console as someone (stores/viewAsStore.ts).
 *
 * Above the shell, not inside the sticky bar: the bar is the 56px datum every
 * page sticky is offset by, so it cannot grow a second line. Here the band
 * takes its own height and the column below it scrolls as before.
 *
 * Nothing on any screen is hidden for the view: the server refuses each write,
 * and the screen that tried says so, in the server's words.
 */
export function ViewAsBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const viewing = useViewAsStore((s) => s.viewing);
  const ended = useViewAsStore((s) => s.ended);
  const exit = useViewAsStore((s) => s.exit);
  const acknowledge = useViewAsStore((s) => s.acknowledge);
  const [leaving, setLeaving] = useState(false);
  const name = useRef('');
  if (viewing) name.current = viewing.name;

  /* A REFUSED WRITE IS SAID ONCE — by the screen that tried it, in the server's
     own sentence ("Viewing as … — read only. Nothing was changed."). This band
     used to toast it too, so every refusal arrived twice (owner, 2026-10-28). */

  /* However the view ended — Exit, the 30 minutes, the server ending it — the
     administrator lands back on the list it was started from. */
  useEffect(() => {
    if (!ended) return;
    if (ended === 'expired') {
      toast.info(
        t('viewAs.expired', "The view ended after {{minutes}} minutes. You're back as yourself.", {
          minutes: VIEW_AS_TTL_MINUTES,
        })
      );
    }
    acknowledge();
    setLeaving(false);
    navigate(VIEW_AS_RETURN);
  }, [ended, acknowledge, navigate, t]);

  if (!viewing) return null;
  return (
    <div
      role="status"
      className="flex shrink-0 items-center justify-between gap-inline border-b border-warning-border bg-warning-surface px-gutter-sm py-notice-y text-copy-sm text-warning-on-surface md:px-gutter"
    >
      <span className="flex min-w-0 items-center gap-tight font-medium">
        <Eye size={ICON.inline} aria-hidden className="shrink-0" />
        <span className="truncate">{t('viewAs.banner', 'Viewing as {{name}} · read only', { name: viewing.name })}</span>
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={leaving}
        aria-label={t('viewAs.exitOf', 'Stop viewing as {{name}}', { name: viewing.name })}
        onClick={() => {
          setLeaving(true);
          void exit('exit');
        }}
      >
        {leaving ? t('viewAs.leaving', 'Leaving…') : t('viewAs.exit', 'Exit')}
      </Button>
    </div>
  );
}
